import React, { useState } from 'react';
import { CalendarEvent } from '../../../../../types/calendar/calendarTypes';
import Label from '../../../yourProperties/Label';
import Icon from '../../../../../muiComponents/Icon';
import IconButton from '../../../../../muiComponents/IconButton';
import { Popover } from '@mui/material';
import Status from '../../../../../components/Status';
import { Link, useHistory } from 'react-router-dom';
import encodeBase64 from '../../../../../utils/encodeBase64';
import SectionTitle from '../../../yourProperties/Title';
import Title from '../../../../components/Title';
import useTranslate from '../../../../../customHooks/useTranslate';
import CopyOnHover from '../../../components/CopyOnHover';
import { BookingListItem } from '../../../../../types/booking/bookingListTypes';
import { formatDate, formatDateTime } from '../../../../../utils/formatDates';
import useLanguage from '../../../../../customHooks/useLanguage';
import useTimeZone from '../../../../../customHooks/useTimeZone';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import Tooltip from '../../../../../muiComponents/Tooltip';
import '../../../../../stylesheets/stylesComponents/calendar.scss';
import routes from '../../../../../utils/commonVariables/routes';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import {
    doubleMD_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../../utils/commonVariables/grid';

interface EventProps {
    event: CalendarEvent<BookingListItem>;
    idHoveredEvent: string | null;
    hoverEvent: (eventId: string) => void;
    unhoverEvent: () => void;
}

const BookingEvent = ({
    event,
    idHoveredEvent,
    hoverEvent,
    unhoverEvent,
}: EventProps) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const history = useHistory();
    const translate = useTranslate();
    const language = useLanguage();
    const timeZone = useTimeZone();

    const {
        Id,
        BookingCode,
        BookingDate,
        CheckInDate,
        CheckOutDate,
        SupplierHotel,
        IdSupplierHotel,
    } = event.extendedProps;

    return (
        <>
            <div
                className={`overflow-hidden flex flex-between gap-5 flex-align-center border-radius-4 cursor-pointer padding-4 ${
                    idHoveredEvent === event.id
                        ? 'fc__hovered-event'
                        : 'border-color'
                }`}
                style={{ height: '30px', maxWidth: '100%' }}
                onClick={() => {
                    const currentPath = history.location.pathname;
                    history.push(
                        `${currentPath}/${encodeBase64(event.extendedProps.Id)}`
                    );
                }}
                onMouseEnter={() => hoverEvent(event.id)}
                onMouseLeave={unhoverEvent}
            >
                <div className='flex gap-5 flex-align-center overflow-hidden'>
                    <Status color={event.extendedProps.StatusColor} />

                    <Label text={event.title} translated={false} />
                </div>
                <IconButton
                    iconName={anchor ? 'VisibilityOff' : 'Visibility'}
                    onClick={(
                        iconEvent: React.MouseEvent<
                            HTMLButtonElement,
                            MouseEvent
                        >
                    ) => {
                        iconEvent.stopPropagation();
                        setAnchor(anchor ? null : iconEvent.currentTarget);
                    }}
                    style={{ padding: '4px' }}
                    iconProps={{ style: { fontSize: '16px' } }}
                />
            </div>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    paper: {
                        borderRadius: '16px',
                    },
                }}
            >
                <div
                    style={{ width: '400px' }}
                    className='flex-column gap-10 padding20'
                >
                    <div className='flex flex-between flex-align-baseline padding-inline15 border-color-bottom padding-bottom-8'>
                        <CopyOnHover textToCopy={BookingCode}>
                            <SectionTitle
                                className='text-secondary-color'
                                text={BookingCode}
                                translated={false}
                            />
                        </CopyOnHover>
                        <Status
                            text={event.extendedProps?.Status}
                            color={event.extendedProps?.StatusColor}
                        />
                    </div>
                    <Grid
                        style={{ width: 'auto', margin: 0 }}
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing20}
                    >
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                    <Icon
                                        iconName={commonIcons.HOTEL}
                                        className='text-secondary-700'
                                    />
                                    <Title className='text-capitalize'>
                                        {translate('hotel')}
                                    </Title>
                                </div>
                                <Tooltip titleText='navigate_to_hotel_detail'>
                                    <p
                                        className='link-onhover cursor-pointer '
                                        onClick={() => {
                                            const route = `${routes.propertyDetail(
                                                IdSupplierHotel
                                            )}`;
                                            window.open(route, route);
                                        }}
                                    >
                                        {SupplierHotel}
                                    </p>
                                </Tooltip>
                            </div>
                        </GridChild>
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                    <Icon
                                        iconName='EventNote'
                                        className='text-secondary-700'
                                    />
                                    <Title>{translate('booking_date')}</Title>
                                </div>
                                <p>
                                    {BookingDate
                                        ? formatDateTime(
                                              BookingDate,
                                              language,
                                              timeZone
                                          )
                                        : '--/--/----'}{' '}
                                </p>
                            </div>
                        </GridChild>
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                    <Icon
                                        iconName='Today'
                                        className='text-secondary-700'
                                    />
                                    <Title className='text-capitalize'>
                                        {translate('check_in')}
                                    </Title>
                                </div>
                                <p>
                                    {CheckInDate
                                        ? formatDate(CheckInDate)
                                        : '--/--/----'}
                                </p>
                            </div>
                        </GridChild>
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                    <Icon
                                        iconName='Event'
                                        className='text-secondary-700'
                                    />
                                    <Title className='text-capitalize'>
                                        {translate('check_out')}
                                    </Title>
                                </div>
                                <p>
                                    {CheckOutDate
                                        ? formatDate(CheckOutDate)
                                        : '--/--/----'}
                                </p>
                            </div>
                        </GridChild>
                    </Grid>
                    <div className='padding-inline15 border-color-top padding-top-8'>
                        <Link
                            className='width-content gap-10 flex flex-baseline'
                            to={`${history.location.pathname}/${encodeBase64(
                                Id
                            )}`}
                        >
                            <Icon iconName='Description' />
                            {translate('go_to_booking_details')}
                        </Link>
                    </div>
                </div>
            </Popover>
        </>
    );
};

export default BookingEvent;
